import React, { useState, useEffect, useRef } from 'react';
import { Save, X, History, Check, AlertTriangle } from 'lucide-react';
import { documentService } from '../services/documentService';

const DocumentEditor = ({ documentId, onClose, onSave }) => {
  const [document, setDocument] = useState(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [version, setVersion] = useState(1);
  const textareaRef = useRef(null);
  
  useEffect(() => {
    const loadDocument = async () => {
      try {
        setLoading(true);
        const documentData = await documentService.getDocument(documentId);
        setDocument(documentData);
        
        // For simplicity, we're using placeholder text since extracting text from PDFs
        // requires more complex server-side processing
        setContent("This is the extracted content of your document. In a production environment, this would be the actual text content extracted from your PDF.");
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading document:', err);
        setError(err.response?.data?.error || 'Failed to load document');
        setLoading(false);
      }
    };
    
    loadDocument();
  }, [documentId]);
  
  const handleSave = async () => {
    try {
      setSaving(true);
      // In a simplified implementation for the demo, just use the content from state
      const updatedContent = content;
      
      // In a real implementation, you would:
      // 1. Send the updated content to the server
      // 2. The server would convert it to PDF or other format
      // 3. The server would save it as a new version
      
      // For this demo, we'll just simulate saving
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update version number
      setVersion(prev => prev + 1);
      
      if (onSave) {
        onSave({
          id: documentId,
          version: version + 1,
          content: updatedContent
        });
      }
      
      setSaving(false);
    } catch (err) {
      console.error('Error saving document:', err);
      setError('Failed to save document');
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <div className="flex items-start mb-4 text-red-400">
          <AlertTriangle size={24} className="mr-2" />
          <div>
            <h3 className="font-medium">Error</h3>
            <p>{error}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600"
        >
          Close
        </button>
      </div>
    );
  }
  
  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold flex items-center">
            Editing: {document?.name}
            <span className="ml-2 px-2 py-1 bg-blue-500 bg-opacity-20 text-blue-400 rounded-full text-xs">
              v{version}
            </span>
          </h2>
          <p className="text-sm text-gray-400">Changes are automatically saved as new versions</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => {}}
            className="p-2 rounded-md bg-gray-700 hover:bg-gray-600 transition-colors flex items-center"
            title="View version history"
          >
            <History size={18} className="mr-1" />
            History
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="p-2 rounded-md bg-blue-600 hover:bg-blue-500 transition-colors flex items-center"
            title="Save document"
          >
            {saving ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={18} className="mr-1" />
                Save
              </>
            )}
          </button>
          <button
            onClick={onClose}
            className="p-2 rounded-md bg-gray-700 hover:bg-gray-600 transition-colors"
            title="Close editor"
          >
            <X size={18} />
          </button>
        </div>
      </div>
      
      <div className="mb-4 rounded-md overflow-hidden border border-gray-700">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="w-full h-96 p-4 bg-gray-700 text-white focus:outline-none focus:border-blue-500"
        />
      </div>
      
      <div className="flex justify-end">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-700 text-gray-300 rounded-md hover:bg-gray-600 transition-colors mr-2"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-500 transition-colors flex items-center"
        >
          {saving ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Check size={18} className="mr-2" />
              Save & Exit
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default DocumentEditor;